import '@xyflow/react/dist/style.css'
import 'reactflow/dist/style.css'
import '~/common/styles/dossbot-icon.css'
import '~/common/styles/generated/tailwind.css'
// Must follow Tailwind: the two resets tie on specificity, so source order decides which wins
// inside `.arp-root`.
import 'arp/design-system/base.css'

import { LicenseInfo } from '@mui/x-license-pro'
import * as Sentry from '@sentry/react'
import { AllEnterpriseModule } from 'ag-grid-enterprise'
import { AgGridProvider } from 'ag-grid-react'
import { registerDossGridDefaults } from 'arp/design-system/integrations/ag-grid'
import i18n from 'i18next'
import Backend from 'i18next-http-backend'
import LogRocket from 'logrocket'
import { createRoot } from 'react-dom/client'
import { initReactI18next } from 'react-i18next'
import { RouterProvider, createBrowserRouter } from 'react-router'
import constants from '~/common/config/config'
import { dossFont } from '~/common/styles/theme'
import { routes } from '~/router/routes'

// --- Global initialization ---

// Must run before anything else so errors thrown during startup are reported. The guest-frame
// telemetry reuses this client rather than initializing its own.
Sentry.init({
  dsn: constants.sentryDsn,
  release: __DOSS_WEB_RELEASE__,
  environment: constants.isProduction ? 'production' : 'staging',
  enabled: constants.isStaging || constants.isProduction,
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  integrations: [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })],
})

LicenseInfo.setLicenseKey(constants.muiLicenseKey)

const agGridModules = [AllEnterpriseModule]

registerDossGridDefaults()

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    backend: { loadPath: '/locales/{{lng}}/{{ns}}.json' },
    detection: { order: ['path', 'queryString', 'cookie'], caches: ['cookie'] },
    fallbackLng: 'en',
    // `design_system` belongs to ARP and is copied into public/ by `yarn gen-i18n`. Loading it
    // up front rather than on demand keeps ARP components from rendering raw keys on first paint.
    ns: ['translation', 'design_system'],
    defaultNS: 'translation',
    react: { useSuspense: false },
    interpolation: { escapeValue: false },
  })

if (constants.isProduction) {
  LogRocket.init(constants.logRocketId, {
    network: {
      requestSanitizer: (request) => {
        request.headers['authorization'] = 'SANITIZED'
        return request
      },
    },
  })
}

// Global font style
const style = document.createElement('style')
style.textContent = `html { font-family: ${dossFont}; }`
document.head.appendChild(style)

// Swap favicon by environment so local/staging/prod tabs are visually distinct
let faviconEnv: 'production' | 'staging' | 'local' = 'local'
if (constants.isProduction) {
  faviconEnv = 'production'
} else if (constants.isStaging) {
  faviconEnv = 'staging'
}
const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
if (favicon) {
  favicon.href = `/favicons/${faviconEnv}.svg`
}

// --- Render ---

const root = document.getElementById('root')
if (!root) {
  throw new Error('Root element not found')
}

const router = createBrowserRouter(routes)
createRoot(root).render(
  <AgGridProvider licenseKey={__AG_GRID_LICENSE_KEY__} modules={agGridModules}>
    <RouterProvider router={router} />
  </AgGridProvider>,
)
